import { NewTag } from "@/components/Tag";
import { cn } from "@/lib/utils";
import { shouldShowAdsSelector } from "@/redux/user/userSlice";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";

import { APIStatus } from "../../../../../api/APIStatus.type";
import { SavedSquad } from "../../../../../api/privateRequests/squad-builder/get-squads";
import {
  binIcon,
  clubsIcon,
  flagIcon,
  leagueIcon,
} from "../../../../../designSystemIcons";
import { loadedContentSelector } from "../../../../../redux/loadedContent/loadedContentSlice";
import { addAlert } from "../../../../../redux/message/messageSlice";
import {
  RatingFilter as RatingFilterType,
  Scope,
  SquadBuilderMode,
} from "../../../../../redux/squadBuilder/squadBuilderUtils";
import { AppDispatch } from "../../../../../redux/store";
import { MultiSelectDropdown } from "../../../../atoms/Dropdown/MultiSelectDropdown";
import { DropdownItem } from "../../../../atoms/Dropdown/MultiSelectDropdownItems";
import { SimpleDropdown } from "../../../../atoms/Dropdown/SimpleDropdown";
import { Input } from "../../../../atoms/InputField/Input";
import { ScopeSelector } from "../../../../molecules/ScopeSelector/ScopeSelector";
import { OnBoardingNumber } from "../../../OnBoarding/OnBoardingNumber";
import { ComponentContainer } from "../../components/ComponentContainer/ComponentContainer";
import {
  ActiveFilter,
  FilterKey,
  TopFilterBar,
} from "../../components/FiltersBar/FiltersBar";
import { SuggestSquadsButton } from "../../components/SuggestSquadsButton/SuggestSquadsButton";
import { PanelHeader } from "../PanelHeader/PanelHeader";

type Props = {
  mode: SquadBuilderMode;
  statusBuildSquad: APIStatus;
  budget: number;
  ratingFilter: RatingFilterType;
  scope: Scope;
  selectedCountries: number[];
  selectedLeagues: number[];
  selectedClubs: number[];
  savedSquads: SavedSquad[];
  handleBuildSquadFulfilled: () => void;
  handleClickedBuildSquads: () => void;
  handleBudgetUpdated: (budget: number) => void;
  handleMinRatingChange: (minRating: number) => void;
  handleMaxRatingChange: (maxRating: number) => void;
  handleScopeChange: (scope: Scope) => void;
  handleSelectedCountry: (id: number) => void;
  handleSelectedLeague: (id: number) => void;
  handleSelectedClub: (id: number) => void;
  handleClearFilter: (key: FilterKey) => void;
  handleClickMoreFilters: () => void;
  numberOfPlayersWithFilters: number;
  handleUpdateFormation: (formationId: string, isTemp: boolean) => void;
  onBoardingStep?: number;
  chemOptimizing?: boolean;
  desktopMode?: boolean;
  loadedSaveSquadIndex?: number;
  handleLoadSquad: (index: number) => void;
  handleLoadSquads: () => void;
  handleAddNewSquad: () => void;
  handleDeleteSquad: (squadId: string) => Promise<void>;
  activeFilters: ActiveFilter[];
  selectedFormation: string;
};

export const InputMode = ({
  mode,
  statusBuildSquad,
  budget,
  // ratingFilter,
  scope,
  selectedCountries,
  selectedLeagues,
  selectedClubs,
  savedSquads,
  handleClearFilter,
  handleUpdateFormation,
  handleBuildSquadFulfilled,
  handleClickedBuildSquads,
  handleBudgetUpdated,
  handleScopeChange,
  handleSelectedCountry,
  handleSelectedLeague,
  handleSelectedClub,
  handleClickMoreFilters,
  onBoardingStep,
  chemOptimizing,
  desktopMode,
  loadedSaveSquadIndex,
  handleLoadSquad,
  handleLoadSquads,
  handleAddNewSquad,
  handleDeleteSquad,
  activeFilters,
  selectedFormation,
}: Props) => {
  const {
    countries: loadedCountries,
    leagues: loadedLeagues,
    clubs: loadedClubs,
    versions: loadedVersions,
    leaguesCountriesClubsStatus,
    versionsStatus,
    formations,
  } = useSelector(loadedContentSelector);

  const shouldShowAds = useSelector(shouldShowAdsSelector);
  const selectedFormationIndex = useMemo(
    () => Object.keys(formations).indexOf(selectedFormation),
    [formations, selectedFormation],
  );

  const formationLabels = useMemo(
    () => Object.values(formations).map((formation) => formation.label),
    [formations],
  );

  const dispatch = useDispatch<AppDispatch>();

  const [countries, setCountries] = useState<DropdownItem[]>([]);
  const [leagues, setLeagues] = useState<DropdownItem[]>([]);
  const [clubs, setClubs] = useState<DropdownItem[]>([]);
  const [pnps, setPnps] = useState<DropdownItem[]>([]);
  const [versions, setVersions] = useState<DropdownItem[]>([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [leagueSearch, setLeagueSearch] = useState("");
  const [clubSearch, setClubSearch] = useState("");
  const [hoveredSavedSquad, setHoveredSavedSquad] = useState<number>(undefined);
  const [savedSquadsInDropdown, setSavedSquadsInDropdown] = useState<
    JSX.Element[]
  >([]);

  useEffect(() => {
    const _savedSquads = savedSquads.map((squad, index) => (
      <div
        className="flex flex-row items-center justify-between"
        key={squad.id}
      >
        <span>{squad.name}</span>
        {(hoveredSavedSquad === index || isMobile) && (
          <div
            className="text-black/30 hover:text-gray-600"
            onClick={(e) => {
              handleDeleteSquad(squad.id)
                .then(() => {
                  dispatch(
                    addAlert({
                      description: "Squad deleted successfully",
                      type: "success",
                    }),
                  );
                })
                .catch(() => {
                  dispatch(
                    addAlert({
                      description: "Failed to delete squad",
                      type: "error",
                    }),
                  );
                });
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <div className="text-gray-300 stroke-gray-300 hover:text-gray-500 hover:stroke-gray-500">
              {binIcon}
            </div>
          </div>
        )}
      </div>
    ));
    setSavedSquadsInDropdown(_savedSquads);
  }, [
    loadedSaveSquadIndex,
    savedSquads,
    hoveredSavedSquad,
    dispatch,
    handleLoadSquads,
    handleDeleteSquad,
  ]);

  useEffect(() => {
    if (leaguesCountriesClubsStatus === "fulfilled") {
      if (countries.length === 0) {
        setCountries(
          loadedCountries.map((country) => ({
            id: country.id,
            text: country.name,
            icon: (
              <img
                className="my-auto w-7"
                src={`${
                  import.meta.env.VITE_APP_ASSETS_BASE_URL
                }/fc25/countries/${country.id}.png?scale=50`}
                alt={country.name}
              />
            ),
          })),
        );
      }
      if (leagues.length === 0) {
        setLeagues(
          loadedLeagues.map((league) => ({
            id: league.id,
            text: league.name + (league.isWomen ? " (w)" : ""),
            icon: (
              <img
                className="w-5 h-4 my-auto"
                src={`${
                  import.meta.env.VITE_APP_ASSETS_BASE_URL
                }/fc25/leagues/${league.id}.png?scale=50`}
                alt={league.name}
              />
            ),
          })),
        );
      }
      if (clubs.length === 0) {
        setClubs(
          loadedClubs.map((club) => ({
            id: club.id,
            text: club.name + (club.isWomen ? " (w)" : ""),
            icon: (
              <img
                className="w-5 h-5 my-auto"
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/clubs/${
                  club.id
                }.png?scale=50`}
                alt={club.name}
              />
            ),
          })),
        );
      }
      if (pnps.length === 0) {
        setPnps(
          loadedClubs.map((club) => ({
            id: club.id,
            text: club.name + (club.isWomen ? " (w)" : ""),
            icon: (
              <img
                className="w-5 h-5 my-auto"
                src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/clubs/${
                  club.id
                }.png?scale=50`}
                alt={club.name}
              />
            ),
          })),
        );
      }
    }
    if (versionsStatus === "fulfilled" && versions.length === 0) {
      setVersions(
        loadedVersions.map((version) => ({
          id: version.id,
          text: version.name,
          icon: (
            <img
              className="w-5 h-5 my-auto"
              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc25/cards/e_${
                version.id
              }_${version.hasLevels ? 3 : 0}.png`}
              alt={version.name}
            />
          ),
        })),
      );
    }
  }, [
    loadedCountries,
    loadedLeagues,
    loadedClubs,
    loadedVersions,
    countries,
    leagues,
    clubs,
    versions,
    leaguesCountriesClubsStatus,
    versionsStatus,
    pnps,
  ]);

  useEffect(() => {
    if (statusBuildSquad === "fulfilled") {
      handleBuildSquadFulfilled();
    }
  }, [statusBuildSquad, dispatch, handleBuildSquadFulfilled]);

  return (
    <>
      {!desktopMode && <PanelHeader title={"Squad Filters"} />}
      <div
        className={cn(
          "relative top-0 flex flex-col items-center w-full py-4 text-white touch-device:absolute sb-v2-lg:px-8",
          shouldShowAds && "touch-device:pb-64 sb-v2-lg:pb-0",
        )}
      >
        <div
          className={clsx("w-full", mode === "best-formation" && "invisible")}
        >
          {desktopMode && (
            <TopFilterBar
              filters={activeFilters}
              onClearFilter={handleClearFilter}
            />
          )}

          <ComponentContainer marginBottom="mb-4 mt-4">
            {savedSquads.length > 0 && (
              <div className="flex flex-col w-full gap-y-1">
                <span className="ml-2 text-xs">Saved Squads</span>
                <div className="flex flex-row gap-x-3">
                  <SimpleDropdown
                    items={savedSquadsInDropdown}
                    selectedItem={loadedSaveSquadIndex}
                    onHover={(index) => setHoveredSavedSquad(index)}
                    onSelect={handleLoadSquad}
                    defaultItem={
                      loadedSaveSquadIndex === undefined ? "Add Squad" : ""
                    }
                    extraButton={
                      <button
                        className="flex items-center justify-center w-full h-[28px] rounded-lg hover:bg-gray-600 mobile:bg-black/30 bg-gray-600 text-white"
                        onClick={(e) => {
                          handleAddNewSquad();
                          e.stopPropagation();
                        }}
                      >
                        Add new +
                      </button>
                    }
                  />
                </div>
              </div>
            )}
            <div className="flex flex-col w-full mt-4 gap-y-1">
              <span className="ml-2 text-xs">Formation</span>
              <div className="flex flex-row gap-x-3">
                <SimpleDropdown
                  items={formationLabels}
                  selectedItem={selectedFormationIndex}
                  onSelect={(index) =>
                    handleUpdateFormation(Object.keys(formations)[index], false)
                  }
                  onHover={(index) =>
                    desktopMode &&
                    handleUpdateFormation(Object.keys(formations)[index], true)
                  }
                />
              </div>
            </div>
          </ComponentContainer>
        </div>
        <ComponentContainer
          title={
            <div className="flex flex-col whitespace-nowrap gap-x-1">
              <span className="text-xs text-secondary">
                Customize AI Squad Suggestions ✨
              </span>
            </div>
          }
        >
          <div className="flex flex-col w-full gap-y-4">
            <div className="flex flex-col w-full mx-auto">
              <span className="h-5 pl-2 text-xs text-secondary">Budget</span>
              <Input
                placeholder="Enter max team price"
                onChange={(budget) => {
                  budget === "" || isNaN(Number(budget))
                    ? handleBudgetUpdated(Number.MAX_VALUE)
                    : handleBudgetUpdated(Number(budget));
                }}
              >
                {budget === Number.MAX_VALUE
                  ? ""
                  : budget.toLocaleString("en-US")}
              </Input>
              {Number(budget) && budget !== Number.MAX_VALUE && (
                <span className="text-gray-400 text-xs mt-1">
                  An extra 10% of budget is used for the AI bench suggestions.
                </span>
              )}
            </div>
            <ScopeSelector active={scope} onClick={handleScopeChange} />
            <div>
              <span className="h-5 pl-2 text-xs text-secondary">
                General Filters
              </span>
              <div className="flex flex-col gap-y-1">
                <MultiSelectDropdown
                  items={
                    countrySearch
                      ? [
                          ...countries.filter((c) =>
                            c.text
                              .toLowerCase()
                              .includes(countrySearch.toLowerCase()),
                          ),
                        ]
                      : countries
                  }
                  onSelect={handleSelectedCountry}
                  placeHolder={"Country"}
                  selectedIds={selectedCountries}
                  onClear={() => handleSelectedCountry(-1)}
                  placeHolderIcon={flagIcon}
                  searchValue={countrySearch}
                  onSearchChange={setCountrySearch}
                  setDropdownItems={setCountries}
                />
                <MultiSelectDropdown
                  items={
                    leagueSearch
                      ? [
                          ...leagues.filter((l) =>
                            l.text
                              .toLowerCase()
                              .includes(leagueSearch.toLowerCase()),
                          ),
                        ]
                      : leagues
                  }
                  onSelect={handleSelectedLeague}
                  placeHolder={"League"}
                  selectedIds={selectedLeagues}
                  onClear={() => handleSelectedLeague(-1)}
                  placeHolderIcon={leagueIcon}
                  searchValue={leagueSearch}
                  onSearchChange={setLeagueSearch}
                  setDropdownItems={setLeagues}
                />
                <MultiSelectDropdown
                  items={
                    clubSearch
                      ? [
                          ...clubs.filter((c) =>
                            c.text
                              .toLowerCase()
                              .includes(clubSearch.toLowerCase()),
                          ),
                        ]
                      : clubs
                  }
                  onSelect={handleSelectedClub}
                  placeHolder={"Club"}
                  selectedIds={selectedClubs}
                  onClear={() => handleSelectedClub(-1)}
                  placeHolderIcon={clubsIcon}
                  searchValue={clubSearch}
                  onSearchChange={setClubSearch}
                  setDropdownItems={setClubs}
                />
                <div className="flex mt-4 relative w-fit mx-auto">
                  <button
                    className="px-8 py-1 mx-auto text-xs rounded-md bg-white/10 hover:bg-white/20"
                    onClick={(e) => {
                      handleClickMoreFilters();
                      e.stopPropagation();
                    }}
                  >
                    More General Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </ComponentContainer>
        {desktopMode && (
          <div
            className={clsx(
              "mt-4 relative",
              onBoardingStep === 1 && "z-21 pointer-events-none",
            )}
          >
            {onBoardingStep === 1 && (
              <div className="absolute -left-14">
                <OnBoardingNumber number={2} />
              </div>
            )}
            <SuggestSquadsButton
              loading={statusBuildSquad === "pending"}
              onClick={handleClickedBuildSquads}
              chemOptimizing={chemOptimizing}
            />
          </div>
        )}
      </div>
    </>
  );
};
